import { useEffect, useState, useRef } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';
import { throttle } from 'lodash-es';

import { Wrapper } from '@klv/utils/wrapper';
import { getBackgroundType } from '@klv/utils/wrapper/functions';
import Button from '@klv/atoms/button';
import Icon from '@klv/atoms/icon';
import { generateClassName } from '@klv/shared/utils/functions';

import * as styles from './styles/anchorLinks.module.scss';

import type { IAnchorLinks } from './types';

export const AnchorLinks = ({
  className = '',
  backgroundColor,
  customPadding = { customPaddingBottom: 'sm', customPaddingTop: 'sm' },
  fadeIn,
  menuLabel,
  anchors,
  ...props
}: IAnchorLinks) => {
  const { t } = useTranslation(); 

  const anchorTargets = anchors.map(({ jumpAnchorId }) => jumpAnchorId).filter((id): id is string => !!id);
  const anchorPositions = useRef<{ id: string; top?: number; bottom?: number; }[]>([]);
  
  const [selected, setSelected] = useState(anchorTargets[0] || '');
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  //if our mobile menu is open, the body should not scroll.
  useEffect(() => {
    if (menuIsOpen) {
      document.body.classList.add(styles.lockScroll);
    } else {
      document.body.classList.remove(styles.lockScroll);
    }
  }, [menuIsOpen]);

  useEffect(() => {
    const adjustAnchorPositions = throttle(() => {
      // get the top and bottom position of each anchor
      anchorPositions.current = anchorTargets.map((anchorId) => {
        const anchor = document.getElementById(anchorId);
        return {
          id: anchorId,
          top: anchor?.getBoundingClientRect().top,
          bottom: anchor?.getBoundingClientRect().bottom,
        };
      }).sort((a, b) => (a.top || 0) - (b.top || 0));

      // find the anchor that is currently in view
      const selectedAnchor = anchorPositions.current.find(({ top, bottom }) => top && bottom && top - 300 <= 0 && bottom + 200 > 0);
      if (selectedAnchor) {
        setSelected(selectedAnchor.id);
      }
    }, 5);

    // listen for scroll events and adjust the anchor positions
    document.addEventListener('scroll', adjustAnchorPositions);

    return () => {
      document.removeEventListener('scroll', adjustAnchorPositions);
    };
  }, [anchorTargets]);

  //scroll to the anchor when clicked
  const goToAnchor = (e: React.MouseEvent<HTMLAnchorElement>, anchorId: string) => {
    e.preventDefault();
    setMenuIsOpen(false);
    document.getElementById(anchorId)?.scrollIntoView({ behavior: 'smooth' });
  };

  //get the direction of the arrow based on the position of the anchor
  const getArrowDirection = (e: React.MouseEvent<HTMLAnchorElement>, anchorId: string) => {
    const anchor = document.getElementById(anchorId);
    if (anchor) {
      const rect = anchor.getBoundingClientRect();
      if (rect.top < 0) {
        e.currentTarget.classList.add(styles.arrowUp);
      } else {
        e.currentTarget.classList.remove(styles.arrowUp);
      }
    }
    return false;
  };

  //scroll to the top of the page
  const handleBackToTop = () => {
    setMenuIsOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Wrapper 
      className={generateClassName([
        className,
        styles.anchorLinks,
        menuIsOpen ? styles.menuIsOpen : '',
        getBackgroundType(backgroundColor) === 'dark' ? styles.anchorLinksDark : ''])}
      customPadding={customPadding}
      fadeIn={fadeIn ? 1 : undefined}
      backgroundColor={backgroundColor}
      {...props}
    >
      <div className="grid">
        <div className={styles.anchorLinkMenu}>
          <button className={styles.openMenuButton} onClick={() => setMenuIsOpen(!menuIsOpen)}>
            <Icon iconName='ChevronNorth' size={2}/>
          </button>
          <div className={styles.anchorLinkMenuLabel}>
            {menuLabel}
          </div>
          <ul className={generateClassName([styles.desktopAnchors])}>
            {anchors.map(({ jumpAnchorLabel, jumpAnchorId }) => jumpAnchorId && (
              <li key={jumpAnchorId}>
                <a 
                onMouseOver={(e) => getArrowDirection(e, jumpAnchorId)}
                onClick={(e) => goToAnchor(e, jumpAnchorId)} href={`#${jumpAnchorId}`} className={selected === jumpAnchorId ? styles.selectedAnchor : ''}>
                <Icon iconName='ArrowDown' size={1.1}/>
                  {jumpAnchorLabel}
                </a>
              </li>
            ))}
          </ul>

          <ul className={generateClassName([styles.mobileAnchors])}>
            {!menuIsOpen && (
              <li>
                <a>
                {anchors.find(({ jumpAnchorId }) => jumpAnchorId === selected)?.jumpAnchorLabel || anchors[0].jumpAnchorLabel}
                </a>
              </li>
            )}
            {menuIsOpen && (
              <>
                {anchors.map(({ jumpAnchorLabel, jumpAnchorId }) => jumpAnchorId && (
                  <li key={jumpAnchorId}>
                    <a 
                    onMouseOver={(e) => getArrowDirection(e, jumpAnchorId)}
                    onClick={(e) => goToAnchor(e, jumpAnchorId)} href={`#${jumpAnchorId}`} className={selected === jumpAnchorId ? styles.selectedAnchor : ''}>
                      <Icon iconName='ArrowDown' size={1.1}/>
                      {jumpAnchorLabel}
                    </a>
                  </li>
                ))}
                <li><Button className={styles.backToTopButton} action={handleBackToTop} variant='primary' text={t('organisms_anchorLinks_backToTop')} /></li>
            </>
            )}
          </ul>
          
        </div>
      </div>
    </Wrapper>
  );
};
